export const ARCHITECTURE_VERSION = "1.1.0";

export const CANONICAL_NAMES = Object.freeze({
  GREAT_UNLOCKIN: "the great (un)lock-in.",
});

export const CANONICAL_ALIASES = Object.freeze({
  [CANONICAL_NAMES.GREAT_UNLOCKIN]: Object.freeze([
    "the great unlocking",
    "great unlocking",
    "the great unlock-in",
    "great unlock-in",
    "the great (un)lock in",
    "great (un)lock-in",
  ]),
});

const field = (type, extra = {}) => Object.freeze({ type, ...extra });
const relation = (recordType, extra = {}) => Object.freeze({ recordType, identity: "canonical-id", ...extra });

export const SYSTEM_ARCHITECTURE = Object.freeze({
  version: ARCHITECTURE_VERSION,
  storage: { provider: "supabase-key-value", parallelDatabaseAllowed: false },
  deferredRecordTypes: ["campaign"],
  recordTypes: Object.freeze({
    project: {
      store: "projects-v3",
      required: ["id", "name", "status"],
      fields: { id: field("string", { systemManaged: true }), name: field("string"), aliases: field("string[]"), status: field("enum", { values: ["active", "paused", "complete", "archived"] }), outcomeId: field("string"), outcomeTitle: field("string", { displaySnapshot: true }), milestones: field("object[]") },
      relationships: { outcomeId: relation("outcome") },
      capabilities: ["plan", "organize", "relate-tasks", "relate-schedule"],
      approvalRequired: true,
    },
    task: {
      store: "tasks-master",
      required: ["id", "name", "status"],
      fields: { id: field("string", { systemManaged: true }), name: field("string"), status: field("enum", { values: ["not started", "in progress", "waiting", "blocked", "done", "archived", "superseded"] }), projectId: field("string"), projectTitle: field("string", { displaySnapshot: true }), outcomeId: field("string"), outcomeTitle: field("string", { displaySnapshot: true }), plannedDate: field("date"), dueDate: field("date"), sourceProposalId: field("string", { systemManaged: true }), handoffOperationId: field("string", { systemManaged: true }) },
      relationships: { projectId: relation("project"), outcomeId: relation("outcome") },
      capabilities: ["work", "schedule", "complete", "reschedule"],
      approvalRequired: true,
    },
    outcome: {
      store: "ceo-focus",
      required: ["id", "title", "status"],
      fields: { id: field("string", { systemManaged: true }), title: field("string"), status: field("enum", { values: ["active", "paused", "complete", "archived"] }), aliases: field("string[]") },
      relationships: {}, capabilities: ["focus", "relate-projects", "relate-tasks"], approvalRequired: true,
    },
    schedule_block: {
      store: "groot-weekly-schedule",
      required: ["id", "title", "date", "status"],
      fields: { id: field("string", { systemManaged: true }), title: field("string"), date: field("date"), startTime: field("time"), endTime: field("time"), status: field("enum", { values: ["proposed", "approved", "complete", "rescheduled", "cancelled", "archived"] }), taskIds: field("string[]"), projectId: field("string"), projectTitle: field("string", { displaySnapshot: true }), outcomeId: field("string"), outcomeTitle: field("string", { displaySnapshot: true }), sourceProposalId: field("string", { systemManaged: true }), handoffOperationId: field("string", { systemManaged: true }) },
      relationships: { taskIds: relation("task", { many: true }), projectId: relation("project"), outcomeId: relation("outcome") },
      capabilities: ["calendar", "time-block", "reschedule"], approvalRequired: true,
    },
    content: {
      store: "content-v3", required: ["id", "title", "status"],
      fields: { id: field("string", { systemManaged: true }), title: field("string"), status: field("enum", { values: ["idea", "planning", "drafted", "ready", "published", "archived"] }) },
      relationships: {}, capabilities: ["develop", "publish"], approvalRequired: true,
    },
    decision: { store: "guardian-decisions", required: ["id", "title"], fields: { id: field("string", { systemManaged: true }), title: field("string"), status: field("string") }, relationships: {}, capabilities: ["remember"], approvalRequired: true },
    memory: { store: "guardian-memory", required: ["id", "content"], fields: { id: field("string", { systemManaged: true }), content: field("string") }, relationships: {}, capabilities: ["retrieve"], approvalRequired: false },
    delegation: { store: "groot-delegation-queue", required: ["id", "title", "status"], fields: { id: field("string", { systemManaged: true }), title: field("string"), status: field("enum", { values: ["queued", "assigned", "done", "cancelled"] }) }, relationships: {}, capabilities: ["delegate"], approvalRequired: true },
  }),
  operationalViews: {
    ceoOverview: { sources: ["task", "schedule_block", "outcome"], horizonDays: 7 },
    tasks: { sources: ["task"] },
    calendar: { sources: ["schedule_block", "task"] },
    projects: { sources: ["project", "task", "schedule_block"] },
  },
});

export function allowedValues(recordType, fieldName) {
  return SYSTEM_ARCHITECTURE.recordTypes[recordType]?.fields?.[fieldName]?.values || [];
}

export function architectureContext() {
  const records = Object.fromEntries(Object.entries(SYSTEM_ARCHITECTURE.recordTypes).map(([name, def]) => [name, {
    required: def.required, statuses: def.fields.status?.values || [], relationships: def.relationships,
    approvalRequired: def.approvalRequired,
  }]));
  return JSON.stringify({ version: ARCHITECTURE_VERSION, identity: "canonical IDs are authoritative; titles are display snapshots", canonicalNames: CANONICAL_NAMES, aliases: CANONICAL_ALIASES, records, deferredRecordTypes: ["campaign"] });
}
